/**
 * SearchableEntityPicker — typeahead dropdown for picking a contact, lead,
 * or any keyed entity, with an inline "Create new" fallback when no
 * match exists (Phase 5 / Section 3).
 *
 * Props:
 *   items: [{ id, label, sub?, raw }]  (caller pre-formats display strings)
 *   value: id | ""
 *   onChange: fn(id, raw)
 *   placeholder: search placeholder
 *   onCreateNew: fn(query) — optional. Renders a "+ Create new …" row at
 *                the bottom when set + query is non-empty + no exact match.
 *   testid: string for data-testid on the input
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Plus, Search } from "lucide-react";

export default function SearchableEntityPicker({
  items = [],
  value = "",
  onChange,
  placeholder = "Search…",
  onCreateNew = null,
  testid = "entity-picker",
  emptyLabel = "Nothing matches",
  // Iter 176.8 (Iratzi — issue d1843473) — bumped from 10 → 50 so the
  // search dropdown surfaces a reasonable population of matches.
  // Above 50 we render a "show more / refine search" footer instead
  // of silently truncating the list.
  maxRows = 50,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const selected = useMemo(() => items.find((x) => x.id === value), [items, value]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, maxRows);
    return items
      .filter((x) => (x.label + " " + (x.sub || "")).toLowerCase().includes(q))
      .slice(0, maxRows);
  }, [items, query, maxRows]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q && items.some((x) => x.label.toLowerCase() === q);
  }, [items, query]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", textAlign: "left",
          padding: "8px 11px", borderRadius: 8,
          border: "0.5px solid var(--border, #ddd)",
          background: "var(--surface, #fff)",
          fontSize: 12, fontFamily: "inherit", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? "var(--text-primary)" : "var(--text-tertiary)" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {selected && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onChange("", null); }}
              data-testid={`${testid}-clear`}
              style={{ padding: 2, borderRadius: 4, display: "flex" }}
              title="Clear"
            >
              <X style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
            </span>
          )}
          <ChevronDown style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
        </span>
      </button>

      {open && (
        <div
          data-testid={`${testid}-menu`}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "var(--surface, #fff)",
            border: "0.5px solid var(--border, #ddd)", borderRadius: 8,
            boxShadow: "0 6px 18px rgba(0,0,0,0.10)", zIndex: 1000,
            maxHeight: 280, overflowY: "auto",
          }}>
          <div style={{ position: "relative", padding: "6px 8px", borderBottom: "0.5px solid var(--border-soft, #eee)" }}>
            <Search style={{ position: "absolute", left: 14, top: 12, width: 11, height: 11, color: "var(--text-tertiary)" }} />
            <input
              data-testid={`${testid}-search`}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              style={{ width: "100%", padding: "5px 6px 5px 22px", border: "0.5px solid var(--border, #ddd)", borderRadius: 6, fontSize: 12, fontFamily: "inherit", outline: "none", background: "var(--page-bg, #f8fafc)", boxSizing: "border-box" }}
            />
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: 12, fontSize: 11.5, color: "var(--text-tertiary)", textAlign: "center" }}>{emptyLabel}</div>
          )}
          {/* Iter 176.8 (Iratzi — issue d1843473) — surface truncation so
              reps know to refine their search if their lead is beyond
              the maxRows window (previously silently hid leads > 10). */}
          {(() => {
            const q = query.trim().toLowerCase();
            const totalMatching = !q
              ? items.length
              : items.filter((x) => (x.label + " " + (x.sub || "")).toLowerCase().includes(q)).length;
            if (totalMatching > filtered.length) {
              return (
                <div
                  data-testid={`${testid}-truncation-hint`}
                  style={{
                    padding: "6px 10px", fontSize: 10, color: "var(--text-tertiary)",
                    background: "var(--page-bg, #f8fafc)",
                    borderBottom: "0.5px solid var(--border-soft, #f1f5f9)",
                  }}
                >
                  Showing {filtered.length} of {totalMatching} matches — refine your search to narrow down.
                </div>
              );
            }
            return null;
          })()}
          {filtered.map((it) => (
            <button
              key={it.id}
              type="button"
              data-testid={`${testid}-opt-${it.id}`}
              onClick={() => { onChange(it.id, it.raw); setOpen(false); setQuery(""); }}
              style={{
                width: "100%", textAlign: "left", padding: "8px 12px",
                border: "none", background: it.id === value ? "var(--blue-light, #eff6ff)" : "transparent",
                cursor: "pointer", fontFamily: "inherit",
                display: "block", borderBottom: "0.5px solid var(--border-soft, #f1f5f9)",
              }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>{it.label}</div>
              {it.sub && <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>{it.sub}</div>}
            </button>
          ))}
          {onCreateNew && query.trim() && !exactMatch && (
            <button
              type="button"
              data-testid={`${testid}-create-new`}
              onClick={() => { onCreateNew(query.trim()); setOpen(false); setQuery(""); }}
              style={{
                width: "100%", textAlign: "left", padding: "9px 12px",
                border: "none", background: "var(--blue-light, #eff6ff)",
                cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 6,
                color: "var(--blue, #2563eb)", fontSize: 12, fontWeight: 600,
              }}>
              <Plus style={{ width: 11, height: 11 }} /> Create new "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
